import React, { useMemo, type ComponentProps } from 'react';
import { isDefined, modifyColor, silenceError } from '@nfctron/nexus-core';
import { cn } from '@nfctron/nexus-react';

import type { CrowdTypes as Types } from './Crowd.types';

/**
 * Crowd component
 * TODO: make more extensible and configurable (Hub x Tickets relation)
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export function Crowd<T extends 'path' | 'rect'>(props: Types.Props<T>) {
	const { crowd, onCrowdSelect, onCrowdUnselect, getCrowdProps, ...rest } = props;

	/** crowd svg element */
	const crowdEl = useMemo(() => {
		if (!isDefined(crowd)) return null;

		/** get crowd props */
		const crowdProps = getCrowdProps(crowd);
		const { unavailable, selected, saleable, soldOut, carted: isCarted } = crowdProps.crowdIs;

		/** create crowd params */
		const params = silenceError(() => {
			/** base crowd params */
			const baseParams: ComponentProps<'rect'> = {
				fill: crowd.category.color,
				stroke: 'none',
				strokeWidth: 0,
			};

			/** selected crowd */
			if (selected) {
				baseParams.fill = modifyColor(crowd.category.color, (c) => c.darken(0.25));
				return baseParams;
			}

			/** unavailable sold out crowd */
			if (soldOut) {
				baseParams.fill = '#d4d4d8';
				baseParams.strokeWidth = 10;
				baseParams.stroke = '#d4d4d8';
				return baseParams;
			}

			/** unavailable not for sale crowd */
			if (!saleable) {
				baseParams.fill = '#d4d4d8';
				baseParams.strokeWidth = 10;
				baseParams.stroke = '#d4d4d8';
				return baseParams;
			}

			/** carted crowd */
			if (isCarted) {
				baseParams.fill = '#FFFFFF00';
				baseParams.stroke = crowd.category.color;
				baseParams.strokeWidth = '5%';
				return baseParams;
			}

			return {
				...baseParams,
				...crowdProps.svgProps,
			};
		});

		/** on click handler */
		const onClick = () => {
			if (selected) return onCrowdUnselect();
			return onCrowdSelect(crowd.crowdId);
		};

		return React.createElement(rest.tag, {
			fill: crowd.category.color,
			stroke: crowd.category.color,
			strokeOpacity: 0.25,
			onClick,
			className: cn(['transition-all', !unavailable && 'cursor-pointer', !selected && 'hover:opacity-90']),
			...rest,
			...params,
		});
	}, [crowd, getCrowdProps, onCrowdSelect, onCrowdUnselect, rest]);

	/** unable to render the crowd due to missing config */
	if (!isDefined(crowdEl)) {
		console.debug(`[${Crowd.displayName}] Unable to render crowd "${rest.id}" due to missing config`);
		return crowdEl;
	}

	return (
		<React.Fragment>
			{/* crowd element */}
			{crowdEl}
		</React.Fragment>
	);
}

/** Crowd component display name */
Crowd.displayName = 'component.Crowd';
